import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Steps,
  message,
  Space,
  Divider,
} from "antd";
import {
  SaveOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import FileUpload from "../../components/FileUpload";
import { api } from "../../services/api";
import { useAuth } from "../../stores/auth";
import type { User, Attachment } from "../../../shared/types";

const { TextArea } = Input;

export default function CaseCreatePage() {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [caseId, setCaseId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [attachments, setAttachments] = useState<Record<string, Attachment[]>>(
    {
      legal_doc: [],
      decision: [],
      opinion: [],
      photo_a: [],
      photo_b: [],
    }
  );
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.getUsers().then(setUsers).catch(console.error);
  }, []);

  const steps = [
    { title: "基本信息", description: "案件基本情况" },
    { title: "化解情况", description: "矛盾化解情况" },
    { title: "介入情况", description: "介入相关" },
    { title: "提交", description: "保存或推送" },
  ];

  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    if (!values.caseName || !values.caseName.trim()) {
      message.error("请输入案件名称");
      setCurrent(0);
      return;
    }
    setLoading(true);
    try {
      if (!caseId) {
        const result = await api.createCase({
          ...values,
          status: "draft",
        });
        setCaseId(result.id);
        message.success("案件已保存为草稿");
      } else {
        await api.updateCase(caseId, values);
        message.success("草稿已更新");
      }
    } catch (error: any) {
      message.error(error.message || "保存失败");
    } finally {
      setLoading(false);
    }
  };

  const handlePush = async () => {
    const assignedTo = form.getFieldValue("assignedTo");
    if (!assignedTo) {
      message.error("请选择指派人");
      return;
    }

    const values = form.getFieldsValue();
    if (!values.caseName || !values.caseName.trim()) {
      message.error("请输入案件名称");
      setCurrent(0);
      return;
    }

    setLoading(true);
    try {
      let currentCaseId = caseId;
      if (!currentCaseId) {
        const result = await api.createCase({
          ...values,
          status: "draft",
        });
        currentCaseId = result.id;
        setCaseId(result.id);
      } else {
        await api.updateCase(currentCaseId, values);
      }

      await api.pushCase(currentCaseId, assignedTo);
      message.success("案件已推送给用户乙");
      navigate("/");
    } catch (error: any) {
      message.error(error.message || "推送失败");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    const values = form.getFieldsValue();
    if (!values.caseName || !values.caseName.trim()) {
      message.error("请输入案件名称");
      setCurrent(0);
      return;
    }
    setLoading(true);
    try {
      let currentCaseId = caseId;
      if (!currentCaseId) {
        const result = await api.createCase({
          ...values,
          status: "draft",
        });
        currentCaseId = result.id;
        setCaseId(result.id);
      } else {
        await api.updateCase(currentCaseId, values);
      }

      await api.archiveCase(currentCaseId);
      message.success("案件已归档");
      navigate("/");
    } catch (error: any) {
      message.error(error.message || "归档失败");
    } finally {
      setLoading(false);
    }
  };

  const userBList = users.filter((u) => u.role === "user_b");

  return (
    <div className="max-w-4xl mx-auto">
      <Card bordered={false}>
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/")}
            className="mb-4"
          >
            返回列表
          </Button>
          <Steps current={current} items={steps} />
        </div>

        <Form
          form={form}
          layout="vertical"
          size="large"
          initialValues={{
            assignedTo: undefined,
          }}
        >
          {/* 步骤1: 基本信息 */}
          <div style={{ display: current === 0 ? 'block' : 'none' }}>
            <h3 className="text-lg font-medium mb-4">案件基本信息</h3>
            <Form.Item
              name="caseName"
              label="案件名称"
              rules={[{ required: true, message: "请输入案件名称" }]}
            >
              <Input placeholder="请输入案件名称" />
            </Form.Item>

            <Form.Item name="caseSituation" label="案件情况">
              <TextArea
                placeholder="请输入案件情况描述"
                rows={4}
                showCount
                maxLength={2000}
              />
            </Form.Item>
          </div>

          {/* 步骤2: 化解情况 */}
          <div style={{ display: current === 1 ? 'block' : 'none' }}>
            <h3 className="text-lg font-medium mb-4">矛盾化解情况</h3>
            <Form.Item name="resolutionInfo" label="化解情况">
              <TextArea
                placeholder="请输入矛盾化解情况"
                rows={4}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Divider />

            <Form.Item label="法律文书">
              <FileUpload
                caseId={caseId || undefined}
                category="legal_doc"
                value={attachments.legal_doc}
                onChange={(files) =>
                  setAttachments((prev) => ({ ...prev, legal_doc: files }))
                }
              />
            </Form.Item>

            <Form.Item label="立案决定书">
              <FileUpload
                caseId={caseId || undefined}
                category="decision"
                value={attachments.decision}
                onChange={(files) =>
                  setAttachments((prev) => ({ ...prev, decision: files }))
                }
              />
            </Form.Item>

            <Form.Item label="鉴定意见书">
              <FileUpload
                caseId={caseId || undefined}
                category="opinion"
                value={attachments.opinion}
                onChange={(files) =>
                  setAttachments((prev) => ({ ...prev, opinion: files }))
                }
              />
            </Form.Item>

            <Form.Item label="相关照片">
              <FileUpload
                caseId={caseId || undefined}
                category="photo_a"
                value={attachments.photo_a}
                onChange={(files) =>
                  setAttachments((prev) => ({
                    ...prev,
                    photo_a: files,
                  }))
                }
                maxCount={10}
              />
            </Form.Item>
          </div>

          {/* 步骤3: 介入情况 */}
          <div style={{ display: current === 2 ? 'block' : 'none' }}>
            <h3 className="text-lg font-medium mb-4">介入情况</h3>
            <Form.Item name="interventionInfo" label="介入情况">
              <TextArea
                placeholder="请输入介入情况"
                rows={4}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Divider />

            <Form.Item label="相关照片">
              <FileUpload
                caseId={caseId || undefined}
                category="photo_b"
                value={attachments.photo_b}
                onChange={(files) =>
                  setAttachments((prev) => ({
                    ...prev,
                    photo_b: files,
                  }))
                }
                maxCount={10}
              />
            </Form.Item>
          </div>

          {/* 步骤4: 提交 */}
          <div style={{ display: current === 3 ? 'block' : 'none' }}>
            <h3 className="text-lg font-medium mb-4">调解结果与提交</h3>
            <Form.Item name="mediationResult" label="调解结果">
              <TextArea
                placeholder="请输入调解结果"
                rows={4}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Divider />

            <Form.Item name="assignedTo" label="指派给用户乙">
              <Select
                placeholder="选择用户乙（可选）"
                allowClear
                options={userBList.map((u) => ({
                  value: u.id,
                  label: u.displayName,
                }))}
              />
            </Form.Item>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 mb-4">
                请选择操作方式：
              </p>
              <ul className="text-gray-500 text-sm mb-4 list-disc list-inside">
                <li>保存草稿：仅保存，稍后继续编辑</li>
                <li>直接归档：保存并归档案件</li>
                <li>推送给用户乙：保存并发送给用户乙办理</li>
              </ul>
            </div>
          </div>
        </Form>

        <Divider />

        <div className="flex justify-between">
          <div>
            {current > 0 && (
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => setCurrent(current - 1)}
              >
                上一步
              </Button>
            )}
          </div>
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSaveDraft}
              loading={loading}
            >
              保存草稿
            </Button>
            {current < steps.length - 1 ? (
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={() => setCurrent(current + 1)}
              >
                下一步
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleArchive}
                  loading={loading}
                >
                  直接归档
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handlePush}
                  loading={loading}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                >
                  推送给用户乙
                </Button>
              </>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
}
